import {useCurrentOrganization, useIsAgencyWorkspace, useSubscriptionPlan} from "@tinct/core-workspace";
import {PlanSummary} from "@tinct/web-components";
import {useOpenPaywall} from "../modal/paywall-pro-required-modal/paywall-pro-required-modal";
import {useSubscriptionUpdate} from "../hooks/use-subscription-update";

export const TinctPlanSummary = () => {

  const { openPaywall } = useOpenPaywall();

  const currentOrganization = useCurrentOrganization();
  // Agency subscriptions are created by the super admin with
  // collection_method=send_invoice, which Stripe's portal update flow refuses —
  // so agencies cannot self-upgrade.
  const isAgency = useIsAgencyWorkspace();
  const { handleSubscriptionUpdate, isPending } = useSubscriptionUpdate();
  const { planName, hasActiveSubscription, subscriptionBadgeStatusProp, subscriptionBadgeStatusLabel } = useSubscriptionPlan();
  const subscription = currentOrganization.subscription;

  const creditsQuota = currentOrganization.activeQuotas?.find(q => q.featureKey === 'lp_generation_credit');
  const lpQuota = currentOrganization.activeQuotas?.find(q => q.featureKey === 'lp_publication');

  const unitAmountInCents = subscription?.items?.[0]?.price?.unitAmountInCents ?? 0;
  const currency = subscription?.items?.[0]?.price?.currency ?? 'usd';

  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(unitAmountInCents / 100);

  const formatUsagePeriod = () => {
    if (!subscription?.currentPeriodStart || !subscription?.currentPeriodEnd) {
      return undefined;
    }

    const start = new Date(subscription.currentPeriodStart);
    const end = new Date(subscription.currentPeriodEnd);

    const formatDate = (date: Date) => date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });

    const year = end.getFullYear();

    return `${formatDate(start)} - ${formatDate(end)}, ${year}`;
  }

  const recurringIntervalCount = subscription?.items?.[0]?.price?.recurringIntervalCount ?? 1;
  const recurringInterval = subscription?.items?.[0]?.price?.recurringInterval ?? 'year';

  const getRecurringLabel = () => {
    const interval = recurringInterval.charAt(0).toUpperCase() + recurringInterval.slice(1);
    if (recurringIntervalCount > 1) {
      return `Every ${recurringIntervalCount} ${interval}s`;
    }
    return `Every ${interval}`;
  }

  return <PlanSummary
    planName={planName}
    isPro={hasActiveSubscription}
    upgradeLabel={(hasActiveSubscription ? 'Upgrade Plan' : 'Upgrade to Pro')}
    upgradeButtonProps={{ disabled: isPending }}
    hideUpgrade={isAgency}
    badgeStatusVariant={subscriptionBadgeStatusProp}
    badgeStatusLabel={subscriptionBadgeStatusLabel}
    usageMetrics={[
      {
        label: 'credits left',
        value: creditsQuota?.remainingQuota ?? 0,
        percent:  creditsQuota?.quotaLimit ? Math.floor(((creditsQuota.quotaLimit - (creditsQuota.remainingQuota ?? 0)) / creditsQuota.quotaLimit) * 100) : 0,
      },
      {
        label: 'page variants left',
        value: lpQuota?.remainingQuota ?? 0,
        percent: lpQuota?.quotaLimit ? Math.floor(((lpQuota.quotaLimit - (lpQuota.remainingQuota ?? 0)) / lpQuota.quotaLimit) * 100) : 0,
      },
    ]}
    price={formattedPrice}
    priceLabel={getRecurringLabel().toLowerCase()}
    included={[
      {
        value: String(creditsQuota?.quotaLimit ?? 0),
        label: 'credits',
      },
      {
        value: String(lpQuota?.quotaLimit ?? 0),
        label: 'page variants',
      },
    ]}
    subscriptionDate={ subscription?.startDate ? new Date(subscription.startDate).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }): '-'}
    usagePeriod={formatUsagePeriod()}
    onUpgrade={() => {
      if (isAgency) return;
      if(!hasActiveSubscription) {
        openPaywall()
      }else {
        handleSubscriptionUpdate()
      }
    }}
  />
}
